import React, { useState, useEffect } from 'react';
import { trailersApi, unitsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Container, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const TRAILER_TYPES = [
  'Dry Van',
  'Reefer',
  'Flatbed',
  'Step Deck',
  'Tanker',
  'Lowboy',
  'Container Chassis',
  'Other',
];

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-700',
  maintenance: 'bg-amber-100 text-amber-700',
};

const emptyForm = {
  trailer_number: '',
  trailer_type: '',
  make: '',
  model: '',
  year: '',
  vin: '',
  license_plate: '',
  status: 'active',
  assigned_unit_id: 'none',
  notes: '',
};

export default function TrailersTab() {
  const { canEdit, isAdmin } = useAuth();
  const [trailers, setTrailers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, uRes] = await Promise.all([
        trailersApi.getAll(),
        unitsApi.getAll(),
      ]);
      setTrailers(tRes.data || []);
      setUnits(uRes.data || []);
    } catch (e) {
      toast.error('Failed to load trailers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.trailer_number.trim()) {
      toast.error('Trailer number is required');
      return;
    }
    try {
      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year, 10) : null,
        trailer_type: formData.trailer_type || null,
        assigned_unit_id: formData.assigned_unit_id === 'none' ? null : formData.assigned_unit_id,
      };
      if (editing) {
        await trailersApi.update(editing.id, payload);
        toast.success('Trailer updated');
      } else {
        await trailersApi.create(payload);
        toast.success('Trailer added');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (t) => {
    setEditing(t);
    setFormData({
      trailer_number: t.trailer_number || '',
      trailer_type: t.trailer_type || '',
      make: t.make || '',
      model: t.model || '',
      year: t.year?.toString() || '',
      vin: t.vin || '',
      license_plate: t.license_plate || '',
      status: t.status || 'active',
      assigned_unit_id: t.assigned_unit_id || 'none',
      notes: t.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trailer?')) return;
    try {
      await trailersApi.delete(id);
      toast.success('Trailer deleted');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = trailers.filter((t) => {
    const s = searchTerm.toLowerCase();
    return (
      t.trailer_number?.toLowerCase().includes(s) ||
      t.trailer_type?.toLowerCase().includes(s) ||
      t.make?.toLowerCase().includes(s) ||
      t.model?.toLowerCase().includes(s) ||
      t.license_plate?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="trailers-tab">
      {/* Header row with Add button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Track non-motorized fleet assets — dry vans, reefers, flatbeds, and more.
        </p>
        {canEdit && (
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-trailer-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Trailer
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editing ? 'Edit Trailer' : 'Add New Trailer'}
                </DialogTitle>
                <DialogDescription>
                  {editing ? 'Update trailer details' : 'Add a trailer to your fleet'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="trailer_number">Trailer # *</Label>
                    <Input
                      id="trailer_number"
                      value={formData.trailer_number}
                      onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                      placeholder="e.g., TRL-001"
                      required
                      data-testid="trailer-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trailer_status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                      <SelectTrigger data-testid="trailer-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trailer_type">Type</Label>
                  <Select
                    value={formData.trailer_type || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, trailer_type: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="trailer-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {TRAILER_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="trailer_make">Make</Label>
                    <Input
                      id="trailer_make"
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="e.g., Wabash"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trailer_model">Model</Label>
                    <Input
                      id="trailer_model"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., DuraPlate"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="trailer_year">Year</Label>
                    <Input
                      id="trailer_year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      placeholder="e.g., 2022"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trailer_plate">License Plate</Label>
                    <Input
                      id="trailer_plate"
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                      placeholder="e.g., TRL-5678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trailer_vin">VIN</Label>
                  <Input
                    id="trailer_vin"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    placeholder="Trailer VIN"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trailer_unit">Assigned Unit</Label>
                  <Select
                    value={formData.assigned_unit_id}
                    onValueChange={(v) => setFormData({ ...formData, assigned_unit_id: v })}
                  >
                    <SelectTrigger data-testid="trailer-unit-select">
                      <SelectValue placeholder="Pick a unit (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not assigned —</SelectItem>
                      {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.unit_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trailer_notes">Notes</Label>
                  <Input
                    id="trailer_notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Anything special about this trailer"
                  />
                </div>

                <DialogFooter className="pt-2 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="trailer-submit-btn"
                  >
                    {editing ? 'Save Changes' : 'Add Trailer'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search trailers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="trailers-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trailer #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>License Plate</TableHead>
                  <TableHead>Assigned Unit</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-muted-foreground">
                      {trailers.length === 0 ? (
                        <div className="flex flex-col items-center gap-2">
                          <Container className="h-8 w-8 text-muted-foreground/50" />
                          <p>No trailers yet. Add your first one!</p>
                        </div>
                      ) : (
                        'No trailers match your search'
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((t) => (
                    <TableRow key={t.id} data-testid={`trailer-row-${t.id}`}>
                      <TableCell className="font-medium">{t.trailer_number}</TableCell>
                      <TableCell className="text-sm">{t.trailer_type || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {[t.year, t.make, t.model].filter(Boolean).join(' ') || '-'}
                      </TableCell>
                      <TableCell>{t.license_plate || '-'}</TableCell>
                      <TableCell className="text-sm">{t.assigned_unit_number || '-'}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[t.status] || statusColors.active}>
                          {t.status || 'active'}
                        </Badge>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => handleEdit(t)}
                              data-testid={`edit-trailer-${t.id}`}
                            >
                              Edit
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(t.id)}
                                data-testid={`delete-trailer-${t.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
